import React, { useEffect, useRef } from 'react';
import { TextField } from '@mui/material';

/**
 * GoogleAddressSearch
 * Uses the Google Maps Places API loaded via script tag in index.html.
 *
 * Props:
 *   value     – current address string (controlled)
 *   onChange  – called with new address string on text change
 *   onSelect  – called with { address, lat, lng } when a place is chosen
 *   label     – TextField label
 */
function GoogleAddressSearch({ value, onChange, onSelect, label = 'Search address' }) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);

  useEffect(() => {
    if (!window.google || !inputRef.current) return;

    autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
      fields: ['formatted_address', 'geometry', 'name'],
    });

    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current.getPlace();
      if (!place?.geometry) return;
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      const address = place.formatted_address || place.name || '';
      onSelect({ address, lat, lng });
    });

    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [onSelect]);

  return (
    <TextField
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      fullWidth
      size="small"
      autoComplete="off"
      inputRef={inputRef}
    />
  );
}

export default GoogleAddressSearch;
